// AI Migration Center client — talks to migration-service through the gateway.
// Flow: create job → upload CSV → AI-suggest mappings → review/override →
// validate (dry-run via employee-service) → approve → execute.
import { gatewayApi } from '@/lib/hrApi';

const BASE = '/api/v1/migrations';

export type JobStatus =
  | 'draft' | 'staged' | 'mapped' | 'validated' | 'approved' | 'executing' | 'completed' | 'failed' | 'rolled_back';

export interface MigrationJob {
  id: string;
  name: string;
  source_type: string;
  target: string;
  status: JobStatus;
  source_headers: string[];
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  created_count: number;
  updated_count: number;
  error_count: number;
  skipped_count: number;
  warning_count: number;
  // reconciled: source rows == created + updated + skipped + errors after
  // execution — false means rows disappeared downstream and needs attention.
  reconciled: boolean;
  fingerprint?: string;
  snapshot_id?: string;
  /** Biometric imports only: the saved export format this upload was read with. */
  source_profile_id?: string | null;
  /** Biometric imports only: the first of the month the day columns belong to. */
  period_month?: string | null;
  last_error?: string;
  created_at: string;
  /** The company this import is for; null = group-wide (visible only to people who reach every company). */
  legal_entity_id?: string | null;
}

export interface Mapping {
  id?: string;
  source_field: string;
  target_field: string;
  confidence: number;
  reason: string;
  review_level: 'high' | 'review' | 'manual';
  origin: string;
  approved: boolean;
}

export interface MigrationRow {
  id: string;
  row_number: number;
  raw: Record<string, string>;
  status: string;
  errors: string[];
  warnings?: string[];
}

export interface ImportResult {
  dry_run: boolean;
  summary: { total: number; to_create: number; to_update: number; skipped: number };
  results: { row: number; email?: string; name?: string; action: string; errors: string[]; warnings: string[] }[];
  child_issues?: string[];
}

// Selectable import destinations (mirrors migration-service target registry).
// NOTE: loans are deliberately absent — legacy external loans are never
// migrated into KaziPeople (kept as reference documents at most).
export const MIGRATION_TARGETS: { key: string; label: string; hint: string }[] = [
  { key: 'employees', label: 'Employees', hint: 'Staff directory — names, roles, IDs, contacts' },
  { key: 'payroll', label: 'Payroll (current compensation)', hint: 'Base salary per employee (employees must already exist)' },
  { key: 'payroll_history', label: 'Payroll history (read-only)', hint: 'Past payslips from your previous system — preserved for reports and history, never paid or re-processed' },
  { key: 'leave', label: 'Leave opening balances', hint: 'Opening leave balance per employee per type (employees must already exist)' },
  { key: 'benefits', label: 'Benefit enrollments', hint: 'Enroll employees in a benefit plan (employees + plans must already exist)' },
  { key: 'attendance', label: 'Attendance (scanner export)', hint: 'Clock in/out data from your gate/biometric system — reviewed before it can reach payroll' },
];

// Canonical employee target fields (mirrors migration-service catalog).
export const TARGET_FIELDS: { key: string; label: string }[] = [
  { key: 'email', label: 'Email' },
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'employee_id', label: 'Employee ID' },
  { key: 'position', label: 'Job title' },
  { key: 'org_level', label: 'Org level' },
  { key: 'department', label: 'Department' },
  { key: 'manager_email', label: 'Manager email' },
  { key: 'hire_date', label: 'Hire date' },
  { key: 'status', label: 'Status' },
  { key: 'phone', label: 'Phone' },
  { key: 'nationality', label: 'Nationality' },
  { key: 'marital_status', label: 'Marital status' },
  { key: 'national_id', label: 'National ID' },
  { key: 'nssf', label: 'NSSF' },
  { key: 'tin', label: 'TIN' },
  { key: 'driver_license', label: 'Driver license' },
  { key: 'dob', label: 'Date of birth' },
];

// Canonical payroll-compensation target fields.
export const PAYROLL_TARGET_FIELDS: { key: string; label: string }[] = [
  { key: 'employee_email', label: 'Employee email' },
  { key: 'employee_id', label: 'Employee ID' },
  { key: 'base_salary', label: 'Base salary' },
  { key: 'currency', label: 'Currency' },
  { key: 'effective_from', label: 'Effective from' },
];

// Canonical leave-balance target fields (opening state + cutover anchor).
export const LEAVE_TARGET_FIELDS: { key: string; label: string }[] = [
  { key: 'employee_email', label: 'Employee email' },
  { key: 'employee_id', label: 'Employee ID' },
  { key: 'leave_type', label: 'Leave type' },
  { key: 'balance_days', label: 'Balance (days)' },
  { key: 'year', label: 'Year' },
  { key: 'as_of_date', label: 'Balance as of (anchors accrual)' },
  { key: 'entitled_days', label: 'Entitled (days)' },
  { key: 'taken_days', label: 'Taken (days)' },
  { key: 'adjusted_days', label: 'Adjustments (days)' },
  { key: 'carried_forward_days', label: 'Carried forward (days)' },
];

// Canonical historical-payroll target fields (read-only history — never paid).
export const PAYROLL_HISTORY_TARGET_FIELDS: { key: string; label: string }[] = [
  { key: 'employee_email', label: 'Employee email' },
  { key: 'employee_id', label: 'Employee ID' },
  { key: 'period', label: 'Payroll period (YYYY-MM)' },
  { key: 'gross_pay', label: 'Gross pay' },
  { key: 'net_pay', label: 'Net pay' },
  { key: 'base_salary', label: 'Base salary' },
  { key: 'allowances', label: 'Allowances' },
  { key: 'bonuses', label: 'Bonuses' },
  { key: 'overtime', label: 'Overtime' },
  { key: 'commissions', label: 'Commissions' },
  { key: 'paye', label: 'PAYE / income tax' },
  { key: 'pension_employee', label: 'Pension (employee)' },
  { key: 'pension_employer', label: 'Pension (employer)' },
  { key: 'other_deductions', label: 'Other deductions' },
  { key: 'payment_status', label: 'Payment status (paid/unpaid/unknown)' },
  { key: 'currency', label: 'Currency' },
  { key: 'legal_entity_id', label: 'Legal entity' },
  { key: 'legacy_run_id', label: 'Legacy run reference' },
  { key: 'legacy_payslip_ref', label: 'Legacy payslip reference' },
];

// Canonical benefit-enrollment target fields.
export const BENEFITS_TARGET_FIELDS: { key: string; label: string }[] = [
  { key: 'employee_email', label: 'Employee email' },
  { key: 'employee_id', label: 'Employee ID' },
  { key: 'plan_name', label: 'Benefit plan' },
  { key: 'coverage_level', label: 'Coverage level' },
  { key: 'start_date', label: 'Start date' },
];

// Canonical attendance target fields (scanner exports).
export const ATTENDANCE_TARGET_FIELDS: { key: string; label: string }[] = [
  { key: 'scanner_employee_id', label: 'Scanner/employee ID' },
  { key: 'badge_number', label: 'Badge number' },
  { key: 'employee_email', label: 'Employee email' },
  { key: 'employee_name', label: 'Employee name' },
  { key: 'date', label: 'Date' },
  { key: 'clock_in', label: 'Clock in' },
  { key: 'clock_out', label: 'Clock out' },
  { key: 'time', label: 'Punch time' },
  { key: 'device_id', label: 'Device' },
  { key: 'location', label: 'Location / branch' },
];

const FIELDS_BY_TARGET: Record<string, { key: string; label: string }[]> = {
  employees: TARGET_FIELDS,
  payroll: PAYROLL_TARGET_FIELDS,
  payroll_history: PAYROLL_HISTORY_TARGET_FIELDS,
  leave: LEAVE_TARGET_FIELDS,
  benefits: BENEFITS_TARGET_FIELDS,
  attendance: ATTENDANCE_TARGET_FIELDS,
};

// fieldsForTarget returns the dropdown options for a job's target (defaults employees).
export const fieldsForTarget = (target?: string) => FIELDS_BY_TARGET[target ?? 'employees'] ?? TARGET_FIELDS;

export const listMigrations = () =>
  gatewayApi.get<{ migrations: MigrationJob[] }>(BASE).then((r) => r.data.migrations ?? []);

/**
 * legalEntityId: the company the import is for. Omit it ONLY for a deliberate
 * group-wide import by someone who reaches every company — the job is then
 * untagged and visible to all-companies users only.
 */
export const createMigration = (name: string, target = 'employees', snapshotId?: string, legalEntityId?: string) =>
  gatewayApi.post<MigrationJob>(BASE, {
    name, target,
    ...(snapshotId ? { snapshot_id: snapshotId } : {}),
    ...(legalEntityId ? { legal_entity_id: legalEntityId } : {}),
  }).then((r) => r.data);

/**
 * Which companies the caller may create an import for — their migration:write
 * reach, from migration-service itself (the same rule create enforces).
 * allCompanies: every company, and a group-wide (untagged) import is allowed.
 */
export interface CreatableCompanyReach { allCompanies: boolean; legalEntityIds: string[] }

export const getCreatableCompanies = () =>
  gatewayApi
    .get<{ all_companies?: boolean; legal_entity_ids?: string[] | null }>(`${BASE}/companies`)
    .then((r): CreatableCompanyReach => ({
      allCompanies: r.data?.all_companies === true,
      legalEntityIds: r.data?.legal_entity_ids ?? [],
    }));

/**
 * Company names by id, for labels only (GET /legal-entities, reach-filtered by
 * payroll). A failure is an empty map — the label falls back to a short id;
 * it never decides what may be chosen.
 */
export const listCompanyNames = (): Promise<Map<string, string>> =>
  gatewayApi
    .get<{ legal_entities?: { id: string; name: string }[] }>('/api/v1/legal-entities')
    .then((r) => new Map((r.data?.legal_entities ?? []).map((le) => [le.id, le.name] as [string, string])))
    .catch(() => new Map<string, string>());

// --- cutover snapshots -------------------------------------------------------
// One customer cutover = one snapshot grouping its migration jobs, with a
// scope, cutoff/go-live dates, a fingerprint-bound approval, a reconciliation
// manifest and a terminal lock.

export type SnapshotStatus =
  | 'draft' | 'ready_for_approval' | 'approved' | 'completed' | 'completed_with_warnings' | 'locked';

export interface MigrationSnapshot {
  id: string;
  name: string;
  source_system: string;
  source_version?: string;
  cutoff_date?: string;
  go_live_date?: string;
  status: SnapshotStatus;
  scope: { domains?: string[]; history?: Record<string, unknown> };
  notes?: string;
  fingerprint?: string;
  created_at: string;
  approved_at?: string;
  locked_at?: string;
}

export interface ManifestEntry {
  job_id: string;
  name: string;
  target: string;
  status: JobStatus;
  source_rows: number;
  valid_rows: number;
  invalid_rows: number;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  warnings: number;
  reconciled: boolean;
}

export interface SnapshotManifest {
  snapshot: MigrationSnapshot;
  jobs: ManifestEntry[];
  source_rows: number;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  warnings: number;
  reconciled: boolean;
  readiness: 'draft' | 'in_progress' | 'ready_for_cutover' | 'completed' | 'locked' | 'attention_needed';
}

export interface ReadinessCheck {
  key: string;
  label: string;
  status: 'READY' | 'NOT_READY' | 'WARNING' | 'UNABLE_TO_VERIFY' | 'NOT_APPLICABLE';
  detail?: string;
}
export interface GoLiveReadiness { snapshot_id: string; ready: boolean; checks: ReadinessCheck[] }

export const listSnapshots = () =>
  gatewayApi.get<{ snapshots: MigrationSnapshot[] }>(`${BASE}/snapshots`).then((r) => r.data.snapshots ?? []);
export const createSnapshot = (s: Partial<MigrationSnapshot>) =>
  gatewayApi.post<MigrationSnapshot>(`${BASE}/snapshots`, s).then((r) => r.data);
export const getSnapshotManifest = (id: string) =>
  gatewayApi.get<SnapshotManifest>(`${BASE}/snapshots/${id}`).then((r) => r.data);
export const getSnapshotReadiness = (id: string) =>
  gatewayApi.get<GoLiveReadiness>(`${BASE}/snapshots/${id}/readiness`).then((r) => r.data);
export const submitSnapshot = (id: string) =>
  gatewayApi.post(`${BASE}/snapshots/${id}/submit`, {}).then((r) => r.data);
export const approveSnapshot = (id: string) =>
  gatewayApi.post(`${BASE}/snapshots/${id}/approve`, {}).then((r) => r.data);
export const completeSnapshot = (id: string) =>
  gatewayApi.post(`${BASE}/snapshots/${id}/complete`, {}).then((r) => r.data);
export const lockSnapshot = (id: string) =>
  gatewayApi.post(`${BASE}/snapshots/${id}/lock`, {}).then((r) => r.data);

export const uploadSource = (id: string, csv: string) =>
  gatewayApi.post<{ job: MigrationJob }>(`${BASE}/${id}/source`, csv, {
    headers: { 'Content-Type': 'text/csv' },
  }).then((r) => r.data.job);

// Excel upload: raw bytes — the backend sniffs the XLSX (zip) magic and parses
// the first sheet; anything else is treated as CSV.
export const uploadSourceBinary = (id: string, bytes: ArrayBuffer) =>
  gatewayApi.post<{ job: MigrationJob }>(`${BASE}/${id}/source`, bytes, {
    headers: { 'Content-Type': 'application/octet-stream' },
  }).then((r) => r.data.job);

// Pull from a connector (google_sheets | url | rest) instead of a file upload.
export const connectSource = (id: string, connector: string, config: Record<string, string>) =>
  gatewayApi.post<{ job: MigrationJob }>(`${BASE}/${id}/connect`, { connector, config }).then((r) => r.data.job);

export const getMigration = (id: string) =>
  gatewayApi.get<{ job: MigrationJob; mappings: Mapping[] }>(`${BASE}/${id}`).then((r) => r.data);

export const getRows = (id: string, limit = 50, offset = 0) =>
  gatewayApi.get<{ rows: MigrationRow[]; total: number }>(`${BASE}/${id}/rows`, { params: { limit, offset } }).then((r) => r.data);

export const suggestMapping = (id: string) =>
  gatewayApi.post<{ mappings: Mapping[] }>(`${BASE}/${id}/suggest`, {}).then((r) => r.data.mappings ?? []);

export const setMappings = (id: string, mappings: Mapping[]) =>
  gatewayApi.put<{ mappings: Mapping[] }>(`${BASE}/${id}/mappings`, { mappings }).then((r) => r.data.mappings ?? []);

export const validateMigration = (id: string) =>
  gatewayApi.post<ImportResult>(`${BASE}/${id}/validate`, {}).then((r) => r.data);

export const approveMigration = (id: string) =>
  gatewayApi.post(`${BASE}/${id}/approve`, {}).then((r) => r.data);

export const executeMigration = (id: string) =>
  gatewayApi.post<ImportResult>(`${BASE}/${id}/execute`, {}).then((r) => r.data);

export interface RollbackResult { batch_id: string; reverted: number; restored: number }
export const rollbackMigration = (id: string) =>
  gatewayApi.post<RollbackResult>(`${BASE}/${id}/rollback`, {}).then((r) => r.data);

// --- learning layer ---
export interface MigrationIntelligence {
  learned_mappings: number;
  avg_confidence: number;
  admin_corrections: number;
  global_patterns: number;
  top_patterns: { source: string; target: string; hits: number }[];
}
export const getIntelligence = () =>
  gatewayApi.get<MigrationIntelligence>(`${BASE}/intelligence/stats`).then((r) => r.data);

export interface MigrationDashboard {
  stats: MigrationIntelligence;
  total_jobs: number;
  completed_jobs: number;
  auto_map_rate: number;
  confidence_trend: { date: string; avg_confidence: number; mapped: number; ai: number }[];
  resolution_mix: { origin: string; count: number; pct: number }[];
  corrections: { source: string; ai_target: string; final_target: string; when: string }[];
  weak_headers: { source: string; corrections: number; seen: number }[];
  target_coverage: { target: string; mappings: number; corrections: number; avg_confidence: number }[];
}
export const getIntelligenceDashboard = () =>
  gatewayApi.get<MigrationDashboard>(`${BASE}/intelligence/dashboard`).then((r) => r.data);
export const clearMigrationHistory = () =>
  gatewayApi.delete(`${BASE}/history`).then((r) => r.data);

// --- biometric attendance import ---
//
// A biometric export is WIDE: one row per employee, one column per day of the
// month. The canonical attendance contract is one row per employee-DAY, so the
// upload needs two things a column mapping cannot express — which columns are
// the day grid, and which month those days are.

export const ATTENDANCE_SHAPE_WIDE_DAY_GRID = 'wide_day_grid';
export const ATTENDANCE_CELL_RANGE_HH_MM = 'range_hh_mm';

export interface AttendanceLayout {
  shape: string;
  /** The column holding the DEVICE's own employee id — not KaziPeople's. */
  id_column: string;
  /** Optional, and only ever a matching hint. */
  name_column?: string;
  /** The day-grid headers in day order: the first is day 1. */
  day_columns: string[];
  cell_format: string;
}

export interface AttendanceSource {
  id: string;
  name: string;
  vendor?: string;
  timezone?: string;
  layout: AttendanceLayout;
  version: number;
  created_at: string;
  updated_at: string;
}

export const listAttendanceSources = () =>
  gatewayApi.get<{ sources: AttendanceSource[] }>(`${BASE}/attendance-sources`).then((r) => r.data.sources ?? []);

export const saveAttendanceSource = (s: Partial<AttendanceSource>) =>
  gatewayApi.put<AttendanceSource>(`${BASE}/attendance-sources`, s).then((r) => r.data);

/** Dana's proposal for which headers are the day grid — a suggestion to confirm. */
export const detectDayColumns = (headers: string[]) =>
  gatewayApi.post<{ day_columns: string[]; found: boolean }>(`${BASE}/attendance-sources/detect`, { headers })
    .then((r) => r.data);

/** period_month is YYYY-MM. The file does not carry it; a person confirms it. */
export const setJobSource = (id: string, sourceProfileId: string, periodMonth: string) =>
  gatewayApi.post<MigrationJob>(`${BASE}/${id}/source-profile`, {
    source_profile_id: sourceProfileId, period_month: periodMonth,
  }).then((r) => r.data);

/** Why a device id is not an employee here. */
export type IdentityDismissReason = 'not_a_person' | 'not_onboarded';

/**
 * `dismissed` means somebody answered "this is not an employee here" — a
 * device account, or a person who is not in the directory yet. ⚠️ It is not a
 * mapping: the days are still attributed to nobody.
 */
export type IdentityResolution = 'confirmed' | 'roster' | 'unresolved' | 'dismissed';

export interface IdentitySuggestion {
  employee_id: string;
  employee_name: string;
  employee_code?: string;
  employee_status?: string;
  /** Why, in words that can be checked against the file. */
  evidence: string;
  /**
   * Device ids this person is ALREADY mapped to on this source. Non-empty
   * means accepting makes this another device for them, not their first.
   */
  held_device_ids?: string[];
}

export interface SourceIdentity {
  external_employee_id: string;
  name_raw?: string;
  day_cells: number;
  rows: number;
  resolution: IdentityResolution;
  employee_id?: string;
  employee_name?: string;
  employee_status?: string;
  /** Confirmed here, but the write to the employee record awaits approval. */
  pending_approval?: boolean;
  note?: string;
  suggestions?: IdentitySuggestion[];
  /** More than one person's name normalises to this row's name. */
  ambiguous?: boolean;
  /**
   * Set when somebody answered "not an employee here".
   * ⚠️ `day_cells` still counts days that reach no payslip.
   */
  dismiss_reason?: 'not_a_person' | 'not_onboarded';
  dismiss_note?: string;
}

export interface IdentityReport {
  source_profile_id: string;
  identities: SourceIdentity[];
  counts: Record<IdentityResolution, number>;
  /** Staged rows whose id column was blank — they can never be attributed. */
  rows_without_id: number;
}

export const getIdentities = (id: string) => identityApi(`${BASE}/${id}`).load();

export interface IdentityCandidate {
  employee_id: string;
  display_name: string;
  employee_code?: string;
  status?: string;
  /** The device id they already carry, so a replacement is visible up front. */
  scanner_employee_id?: string;
}

export interface IdentityCandidatePage {
  items: IdentityCandidate[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export const searchIdentityCandidates = (id: string, q: string, page = 1, pageSize = 20) =>
  identityApi(`${BASE}/${id}`).search(q, page, pageSize);

export interface ConfirmIdentityResult {
  link: { external_employee_id: string; employee_id: string; applied_at?: string | null };
  employee_name?: string;
  /** Set when the device id is queued for change approval, NOT applied yet. */
  change_request_id?: string;
  message: string;
}

/**
 * What to do when the chosen person already carries a different device id.
 *
 * Three outcomes, not two. Somebody enrolled on two devices could only ever
 * keep one under the old refuse/replace pair, and confirming the second
 * silently dropped the first device's days.
 */
export type IdentityResolutionChoice = '' | 'replace' | 'additional' | 'handover';

/**
 * resolution: '' refuses a collision and explains it (the default, and what
 * surfaces the question in the first place); 'replace' retires the id already
 * on the record; 'additional' keeps both, because the person uses two
 * devices — it deliberately leaves their primary device id alone.
 */
export const confirmIdentity = (
  id: string, externalEmployeeId: string, employeeId: string,
  resolution: IdentityResolutionChoice = '', from?: string,
) => identityApi(`${BASE}/${id}`).confirm(externalEmployeeId, employeeId, resolution, from);

/**
 * Records "this device id is not an employee here".
 *
 * ⚠️ Attributes nothing and pays nobody — the scans stay unmatched. What
 * changes is that the row stops being asked about and stops raising a
 * payroll-blocking finding.
 */
export const dismissIdentity = (
  id: string, externalEmployeeId: string,
  reason: 'not_a_person' | 'not_onboarded', note = '',
) => identityApi(`${BASE}/${id}`).dismiss(externalEmployeeId, reason, note);

/** Takes the answer back; the id returns to unresolved. */
export const undismissIdentity = (id: string, externalEmployeeId: string) =>
  identityApi(`${BASE}/${id}`).undismiss(externalEmployeeId);

/** Removes the mapping. It does NOT clear the employee's device id. */
export const forgetIdentity = (id: string, externalEmployeeId: string) =>
  identityApi(`${BASE}/${id}`).forget(externalEmployeeId);

/**
 * "Who is who" endpoints under one root. ONE matching, two places to do it:
 * an upload in the Migration Center (/api/v1/migrations/{job}) or a live
 * BioTime connector on the Attendance page
 * (/api/v1/integrations/biotime/connectors/{connection}). Same shapes, same
 * rules, same screen — the server resolves both through the same matcher.
 */
export interface IdentityReportLive extends IdentityReport {
  /** Live connector only: punches just attributed because their code is matched now. */
  reattributed?: number;
  /** Live connector only: whether BioTime's employee list (names) has arrived. */
  directory_received?: boolean;
}

export function identityApi(root: string) {
  const enc = encodeURIComponent;
  return {
    load: () => gatewayApi.get<IdentityReportLive>(`${root}/identities`).then((r) => r.data),
    search: (q: string, page = 1, pageSize = 20) =>
      gatewayApi.get<IdentityCandidatePage>(`${root}/identity-candidates`, {
        params: { q, page, page_size: pageSize },
      }).then((r) => r.data),
    confirm: (externalEmployeeId: string, employeeId: string, resolution: IdentityResolutionChoice = '', from?: string) =>
      gatewayApi.post<ConfirmIdentityResult>(`${root}/identities`, {
        external_employee_id: externalEmployeeId, employee_id: employeeId,
        resolution,
        // The first day a handed-over code is this person's (YYYY-MM-DD).
        ...(from ? { from } : {}),
        // The older boolean, still sent so a gateway or service that has not
        // picked up `resolution` yet keeps behaving the way it did.
        replace: resolution === 'replace',
      }).then((r) => r.data),
    dismiss: (externalEmployeeId: string, reason: 'not_a_person' | 'not_onboarded', note = '') =>
      gatewayApi.post(`${root}/identities/${enc(externalEmployeeId)}/dismiss`, { reason, note }).then((r) => r.data),
    undismiss: (externalEmployeeId: string) =>
      gatewayApi.delete(`${root}/identities/${enc(externalEmployeeId)}/dismiss`).then((r) => r.data),
    forget: (externalEmployeeId: string) =>
      gatewayApi.delete(`${root}/identities/${enc(externalEmployeeId)}`).then((r) => r.data),
  };
}

export type IdentityApi = ReturnType<typeof identityApi>;
